import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { observer } from 'mobx-react-lite';
import { Button, Loading } from '@linktivity/link-ui';
import { Left } from '@linktivity/link-icons';

import {
  SelectCalendar,
  WarningMessages
} from '@link/china-railway/components';
import { useStore } from '@link/china-railway/stores';
import { getLanguageCode, REBOOK_STATUS } from '@link/china-railway/utils';

import styles from './railway.module.css';
import { RailwayOrderStatus } from '@link/china-railway/services/types';

const ChinaRailwayView = () => {
  const { bookingId = '', token = '' } = useParams();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const { chinaRailway } = useStore();
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { bookingInfo, dateOptions } = chinaRailway;
  const navigate = useNavigate();

  // Rebook mode: from order detail "rebook" button, should not redirect to order detail
  const isRebookMode = useMemo(() => {
    return searchParams.get('rebook') === REBOOK_STATUS;
  }, [searchParams]);

  const issuedTicketStatus = useMemo(() => {
    return [
      RailwayOrderStatus.Issued,
      RailwayOrderStatus.Used,
      RailwayOrderStatus.Cancelling,
      RailwayOrderStatus.Cancelled,
      RailwayOrderStatus.Reserved,
      RailwayOrderStatus.Issuing,
      RailwayOrderStatus.Closed
    ] as RailwayOrderStatus[];
  }, []);
  useEffect(() => {
    setLoading(true);
    chinaRailway
      .getBookingData({
        bookingId,
        token,
        languageCode: getLanguageCode(i18n.language)
      })
      .finally(() => {
        setLoading(false);
        // redirect to order detail if issued ticket and not in rebook mode
        const status = chinaRailway?.bookingInfo?.status as RailwayOrderStatus;
        if (!isRebookMode && issuedTicketStatus.includes(status)) {
          navigate(`/order-detail/${bookingId}/${token}`);
        }
      });
  }, [
    bookingId,
    chinaRailway,
    i18n.language,
    isRebookMode,
    issuedTicketStatus,
    navigate,
    token
  ]);

  useEffect(() => {
    chinaRailway.setBookingId(bookingId);
    chinaRailway.setToken(token);
  }, [bookingId, token, chinaRailway]);

  useEffect(() => {
    const ticketUrl = searchParams.get('t_url');
    if (ticketUrl) {
      chinaRailway.setTicketUrl(decodeURIComponent(ticketUrl));
    }
  }, [searchParams, chinaRailway]);

  // Auto-select first available date
  useEffect(() => {
    if (dateOptions?.length > 0 && !selectedDate) {
      setSelectedDate(dateOptions[0].value);
    }
  }, [dateOptions, selectedDate]);

  const handleBooking = () => {
    if (selectedDate) {
      navigate(`/train-list/${selectedDate}`);
    }
  };

  const handleOpenCalendar = useCallback(() => {
    setCalendarOpen(true);
  }, []);

  const handleCloseCalendar = useCallback(() => {
    setCalendarOpen(false);
  }, []);

  const handleCalendarDateSelect = useCallback((date: Date) => {
    const dateString = format(date, 'yyyy-MM-dd');
    setSelectedDate(dateString);
    setCalendarOpen(false);
  }, []);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <WarningMessages />
      <div className={styles.bookingCard}>
        <div className={styles.routeSection}>
          {chinaRailway.ticketUrl && (
            <a href={chinaRailway.ticketUrl} className={styles.backButton}>
              <Left className={styles.backIcon} />
            </a>
          )}
          <h1 className={styles.routeTitle}>
            {bookingInfo.planInfo?.fromStationName}
            {' → '}
            {bookingInfo.planInfo?.toStationName}
          </h1>
        </div>

        <div className={styles.dateSection} onClick={handleOpenCalendar}>
          <span className={styles.dateLabel}>
            {t('views.railway.ticketCount')}
          </span>
          <div className={styles.ticketInfo}>
            {t(`seatClass.${bookingInfo.planInfo?.seatClass}`)}
            <span className={styles.ticketInfoCount}>
              ×{bookingInfo?.orderInfo?.passengers?.length}
            </span>
          </div>
        </div>
        <div className={styles.dateSection} onClick={handleOpenCalendar}>
          <span className={styles.dateLabel}>
            {t('views.railway.selectDate')}
          </span>
          <span className={styles.dateValue}>{selectedDate || '-'}</span>
        </div>

        <div className={styles.bookingButtonContainer}>
          <Button
            className={styles.bookingButton}
            onClick={handleBooking}
            disabled={!selectedDate}
          >
            {t('views.railway.bookTicket')}
          </Button>
        </div>
      </div>

      <SelectCalendar
        open={calendarOpen}
        onClose={handleCloseCalendar}
        title={t('views.railway.selectDate')}
        value={selectedDate ? parseISO(selectedDate) : undefined}
        onChange={handleCalendarDateSelect}
        dateOptions={chinaRailway.calendarDateOptions}
        preDateOptions={chinaRailway.calendarPreDateOptions}
      />
    </div>
  );
};

const ChinaRailway = observer(ChinaRailwayView);

export default ChinaRailway;
